import * as React from 'react';

import { cx } from '../../utils/cx';

import HintText from './HintText';

// Must match NORMALIZATION_MAP in packages/api/src/controllers/OTPController.ts
const OTP_NORMALIZATION_MAP: Record<string, string> = {
  B: '8',
  D: '0',
  G: '6',
  I: '1',
  L: '1',
  O: '0',
  Q: '9',
  S: '5',
  U: 'V',
  Z: '2',
};

const normalizeOtpChar = (ch: string): string => OTP_NORMALIZATION_MAP[ch] ?? ch;

interface IOTPInputProps {
  /** Number of input fields. @default 6 */
  length?: number;
  /** Current OTP value */
  value: string;
  /** Called when value changes */
  onChange: (value: string) => void;
  /** Called when all fields are filled */
  onComplete?: (code: string) => void;
  /** Whether the input is in an error state */
  isInvalid?: boolean;
  /** Error message to display below the input */
  errorMessage?: string;
  /** Whether the input is disabled */
  isDisabled?: boolean;
  /** Whether to auto-focus the first input on mount */
  autoFocus?: boolean;
}

const OTPInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  isInvalid,
  errorMessage,
  isDisabled,
  autoFocus,
}: IOTPInputProps) => {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  const chars = value.split('').concat(Array(length).fill('')).slice(0, length);

  // Auto-focus first input on mount
  React.useEffect(() => {
    if (autoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus]);

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const updateValue = React.useCallback(
    (newChars: string[]) => {
      const newValue = newChars.join('');
      onChange(newValue);
      if (newValue.length === length && onComplete) {
        onComplete(newValue);
      }
    },
    [onChange, onComplete, length]
  );

  const handleInput = React.useCallback(
    (index: number, inputValue: string) => {
      // Take only the last character typed (handles overtype)
      const char = normalizeOtpChar(inputValue.slice(-1).toUpperCase());
      if (!char) {
        return;
      }

      const newChars = [...chars];
      newChars[index] = char;
      updateValue(newChars);

      // Auto-advance to next field
      if (index < length - 1) {
        focusInput(index + 1);
      }
    },
    [chars, length, updateValue]
  );

  const handleKeyDown = React.useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();
        const newChars = [...chars];
        if (chars[index]) {
          // Clear current field
          newChars[index] = '';
          updateValue(newChars);
        } else if (index > 0) {
          // Move to previous field and clear it
          newChars[index - 1] = '';
          updateValue(newChars);
          focusInput(index - 1);
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        focusInput(index + 1);
      }
    },
    [chars, length, updateValue]
  );

  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData
        .getData('text')
        .replace(/[\s-]/g, '')
        .toUpperCase()
        .split('')
        .map(normalizeOtpChar)
        .join('')
        .slice(0, length);

      if (pasted.length === 0) {
        return;
      }

      const newChars = [...chars];
      for (let i = 0; i < pasted.length; i++) {
        newChars[i] = pasted[i];
      }
      updateValue(newChars);

      // Focus the field after the last pasted character, or the last field
      const focusIndex = Math.min(pasted.length, length - 1);
      focusInput(focusIndex);
    },
    [chars, length, updateValue]
  );

  return (
    <div className="flex w-fit flex-col items-center gap-1.5">
      <div className="flex gap-2">
        {Array.from({ length }, (_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="text"
            maxLength={2}
            autoComplete="one-time-code"
            disabled={isDisabled}
            value={chars[index]}
            onChange={(e) => handleInput(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            aria-label={`Code digit ${index + 1}`}
            className={cx(
              'bg-primary text-primary text-display-xs outline-hidden h-14 w-12 rounded-lg text-center font-semibold ring-1 ring-inset transition-shadow duration-100 ease-linear',
              'shadow-xs ring-primary focus:ring-brand focus:ring-2',
              isInvalid && 'ring-error_subtle focus:ring-error focus:ring-2',
              isDisabled && 'bg-disabled_subtle ring-disabled text-disabled cursor-not-allowed'
            )}
          />
        ))}
      </div>
      {errorMessage && <HintText isInvalid>{errorMessage}</HintText>}
    </div>
  );
};

OTPInput.displayName = 'OTPInput';

export default OTPInput;
